import { createContext, useContext } from 'react'

/**
 * The app ships two parallel route trees over the same pages:
 *   - `/projects/*` — the full app, wrapped in the AppShell sidebar layout.
 *   - `/p/*`        — the embeddable, chrome-free version rendered inside a cross-site iframe.
 *
 * Both trees render the SAME page components; only the surrounding layout (and a few in-page
 * affordances) differ. `EmbedContext` carries which tree we're in down to every Link / navigate
 * call so links resolve to the matching tree instead of breaking the iframe out to the full app.
 */
export const EmbedContext = createContext(false)

/** True when rendering inside the embeddable `/p/*` iframe layout. */
export function useEmbedded(): boolean {
  return useContext(EmbedContext)
}

/** True for pathnames in the embeddable `/p/*` route tree. */
export function isEmbeddedPath(pathname: string): boolean {
  return pathname === '/p' || pathname.startsWith('/p/')
}

/**
 * Rewrites a project-scoped path into the embedded tree: `/projects/<id>/…` → `/p/<id>/…`.
 * The bare `/projects` list has no embedded counterpart, and non-project paths (`/admin`,
 * `/guidance`, `/insights`, `/`, …) are left untouched — they don't exist inside the embed.
 */
export function toEmbeddedPath(path: string): string {
  if (path === '/projects' || path === '/projects/') return path
  if (path.startsWith('/projects/')) return `/p/${path.slice('/projects/'.length)}`
  return path
}

/** Rewrites an embedded path back into the full tree: `/p/<id>/…` → `/projects/<id>/…`. */
export function toFullPath(path: string): string {
  if (path === '/p' || path === '/p/') return '/projects'
  if (path.startsWith('/p/')) return `/projects/${path.slice('/p/'.length)}`
  return path
}

/** Resolves a `/projects`- or `/p`-rooted path to the tree currently in effect. */
export function resolvePath(path: string, embedded: boolean): string {
  return embedded ? toEmbeddedPath(path) : toFullPath(path)
}
